import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { listOrganizationProfilesV1, bulkUpsertUsersV1 } from '../../libs/fetch.js';
import { primaryButtonClasses, secondaryButtonClasses } from '../../libs/sharedClassNames.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_DOMAINS = [];
const listUsersInFlightByOrg = new Map();

function normalizeEmail(raw) {
  if (raw == null || typeof raw !== 'string') return '';
  return raw.trim().toLowerCase();
}

function parseEmailList(text) {
  if (!text || typeof text !== 'string') return [];
  const parts = text.split(/[\n,;\t]+/).map((p) => p.trim()).filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const sub = p.split(/\s+/).filter((s) => s.includes('@'));
    const candidates = sub.length ? sub : [p];
    for (const c of candidates) {
      const n = normalizeEmail(c);
      if (n && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
  }
  return out;
}

function parseCsvUsers(text) {
  if (!text || typeof text !== 'string') return { rows: [], error: 'Paste at least one CSV row.' };
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { rows: [], error: 'Paste at least one CSV row.' };

  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const commaCount = (line.match(/,/g) || []).length;
    if (commaCount !== 1) {
      return { rows: [], error: `Invalid row ${i + 1}: expected exactly 2 columns (email,name).` };
    }
    const [rawEmailPart, rawNamePart] = line.split(',');
    const rawEmail = String(rawEmailPart ?? '').trim().replace(/^"|"$/g, '');
    const rawName = String(rawNamePart ?? '').trim().replace(/^"|"$/g, '');
    const email = normalizeEmail(rawEmail);
    const lowerEmail = email.toLowerCase();

    // Allow optional header row.
    if (lowerEmail === 'email' && rawName.toLowerCase() === 'name') continue;

    if (!email || !rawName) {
      return { rows: [], error: `Invalid row ${i + 1}: both email and name are required.` };
    }
    if (!EMAIL_RE.test(email)) {
      return { rows: [], error: `Invalid row ${i + 1}: invalid email format.` };
    }

    out.push({ email, name: rawName });
  }

  if (!out.length) return { rows: [], error: 'No importable rows found.' };
  return { rows: out, error: null };
}

function profileStatus(p) {
  if (!p.is_active) return 'deactivated';
  if (p.requires_password_set) return 'pending';
  return 'active';
}

/** Display "First Last" from stored parts */
function fullNameFromParts(first, last) {
  return [first, last]
    .map((x) => (x ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** First token before first space = first name; remainder = last name */
function splitFullName(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return { first_name: '', last_name: '' };
  const i = t.indexOf(' ');
  if (i === -1) return { first_name: t, last_name: '' };
  return { first_name: t.slice(0, i).trim(), last_name: t.slice(i + 1).trim() };
}

function formatLastSignIn(iso) {
  if (iso == null || iso === '') return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

function emailDomain(email) {
  const at = email.lastIndexOf('@');
  if (at < 0) return '';
  return email.slice(at + 1).toLowerCase();
}

/** These org email domains do not count toward the analytics edit seat limit (UI + validation). */
const ANALYTICS_LIMIT_EXEMPT_DOMAINS = new Set(['muffindata.com', 'confidotech.com']);

function isAnalyticsLimitExemptEmail(email) {
  const d = emailDomain(normalizeEmail(email));
  return ANALYTICS_LIMIT_EXEMPT_DOMAINS.has(d);
}

function domainAllowed(email, associatedDomains) {
  if (!associatedDomains || associatedDomains.length === 0) return false;
  const d = emailDomain(email);
  return associatedDomains.some((ad) => String(ad).toLowerCase() === d);
}

function permValue(v) {
  if (v == null || v === '') return '';
  return v === 'view' || v === 'edit' ? v : '';
}

function mapProfileToRow(p, idx) {
  return {
    _rowKey: p.id || `row-${idx}`,
    id: p.id,
    email: normalizeEmail(p.email),
    first_name: p.first_name ?? '',
    last_name: p.last_name ?? '',
    is_active: p.is_active !== false,
    requires_password_set: p.requires_password_set,
    last_sign_in_at: p.last_sign_in_at ?? null,
    user_management_permission: permValue(p.user_management_permission),
    product_permission: permValue(p.product_permission),
    integration_permission: permValue(p.integration_permission),
    analytics_permission: permValue(p.analytics_permission),
    created_at: p.created_at,
    _isNew: false,
    _nameInput: null,
    _rowError: null,
    _fieldErrors: null,
  };
}

function newDraftRow(key) {
  return {
    _rowKey: key,
    id: null,
    email: '',
    first_name: '',
    last_name: '',
    is_active: true,
    requires_password_set: true,
    user_management_permission: '',
    product_permission: 'view',
    integration_permission: 'view',
    analytics_permission: '',
    created_at: null,
    last_sign_in_at: null,
    _isNew: true,
    _nameInput: null,
    _rowError: null,
    _fieldErrors: null,
  };
}

function rowBaseline(r) {
  return {
    first_name: r.first_name,
    last_name: r.last_name,
    is_active: r.is_active,
    user_management_permission: r.user_management_permission,
    product_permission: r.product_permission,
    integration_permission: r.integration_permission,
    analytics_permission: r.analytics_permission,
  };
}

const NEW_ROW_DEFAULTS = {
  email: '',
  first_name: '',
  last_name: '',
  is_active: true,
  user_management_permission: '',
  product_permission: 'view',
  integration_permission: 'view',
  analytics_permission: '',
};

/** New draft rows that still match defaults and have no name/email count as untouched (no validation noise). */
function newDraftIsTouched(r) {
  if (!r._isNew) return false;
  return Boolean(
    normalizeEmail(r.email) ||
      fullNameFromParts(r.first_name, r.last_name).trim() ||
      r.user_management_permission !== NEW_ROW_DEFAULTS.user_management_permission ||
      r.product_permission !== NEW_ROW_DEFAULTS.product_permission ||
      r.integration_permission !== NEW_ROW_DEFAULTS.integration_permission ||
      r.analytics_permission !== NEW_ROW_DEFAULTS.analytics_permission ||
      r.is_active !== NEW_ROW_DEFAULTS.is_active
  );
}

/** Small floating validation hint; absolutely positioned so it does not shift row layout. */
function ValidationPopunder({ message }) {
  if (!message) return null;
  return (
    <div
      className="absolute left-0 top-full z-50 mt-1 max-w-[min(22rem,calc(100vw-2rem))] rounded-md border border-red-200 bg-white px-2 py-1.5 text-xs leading-snug text-red-700 shadow-lg"
      role="status"
    >
      {message}
    </div>
  );
}

function nameFieldsChanged(row, baselineById) {
  const cur = fullNameFromParts(row.first_name, row.last_name);
  if (row._isNew) return Boolean(cur);
  const base = baselineById[row.id];
  if (!base) return false;
  return cur !== fullNameFromParts(base.first_name, base.last_name);
}

function editableNameValue(row) {
  if (typeof row._nameInput === 'string') return row._nameInput;
  return fullNameFromParts(row.first_name, row.last_name);
}

/** Which editable/display fields differ from saved baseline or new-row defaults */
function isFieldChanged(row, field, baselineById) {
  if (row._isNew) {
    if (field === 'email') return Boolean(normalizeEmail(row.email));
    if (field === 'first_name' || field === 'last_name') {
      return nameFieldsChanged(row, baselineById);
    }
    if (field === 'is_active') return row.is_active !== NEW_ROW_DEFAULTS.is_active;
    return row[field] !== NEW_ROW_DEFAULTS[field];
  }
  const base = baselineById[row.id];
  if (!base) return false;
  if (field === 'email') return false;
  if (field === 'last_sign_in_at') return false;
  return row[field] !== base[field];
}

/** Background only — inset rings on table cells caused stray dark edge lines in some layouts. */
const CELL_CHANGED = 'bg-amber-50';

function buildInviteeForUpdate(email, baseline, current) {
  const out = { email: normalizeEmail(email) };
  if (current.first_name !== baseline.first_name) {
    out.first_name = current.first_name.trim() || null;
  }
  if (current.last_name !== baseline.last_name) {
    out.last_name = current.last_name.trim() || null;
  }
  if (current.is_active !== baseline.is_active) {
    out.is_active = current.is_active;
  }
  const permKeys = [
    'analytics_permission',
    'integration_permission',
    'product_permission',
    'user_management_permission',
  ];
  for (const k of permKeys) {
    if (current[k] !== baseline[k]) {
      out[k] = current[k] === '' ? null : current[k];
    }
  }
  return out;
}

function countAnalyticsEditSeats(rows) {
  return rows.filter((r) => {
    if (r.analytics_permission !== 'edit') return false;
    if (isAnalyticsLimitExemptEmail(r.email)) return false;
    if (r._isNew) return true;
    if (r.is_active === false) return false;
    return profileStatus(r) !== 'deactivated';
  }).length;
}

/**
 * Client-side validation for all rows (used for live UI and before save).
 * @returns {{ issues: Record<string, { row?: string, name?: string }> }}
 */
function analyzeRows(rows, associatedDomains) {
  const issues = {};
  const emailsSeen = new Set();

  const bump = (rowKey, part) => {
    issues[rowKey] = { ...issues[rowKey], ...part };
  };

  for (const r of rows) {
    if (r._isNew && !newDraftIsTouched(r)) continue;

    const em = normalizeEmail(r.email);
    if (!em) {
      bump(r._rowKey, { row: 'Email is required.' });
      continue;
    }
    if (!EMAIL_RE.test(em)) {
      bump(r._rowKey, { row: 'Invalid email format.' });
      continue;
    }
    if (emailsSeen.has(em)) {
      bump(r._rowKey, { row: 'Duplicate email in this table.' });
      continue;
    }
    emailsSeen.add(em);
    if (r._isNew && !domainAllowed(em, associatedDomains)) {
      bump(r._rowKey, { row: `Email domain must be one of: ${associatedDomains.join(', ')}` });
      continue;
    }

    const fn = (r.first_name ?? '').trim();
    const ln = (r.last_name ?? '').trim();
    const rawCombined = fullNameFromParts(r.first_name, r.last_name).trim();
    if (!rawCombined) {
      bump(r._rowKey, {
        name: 'Name is required.',
      });
    } else if (!ln) {
      bump(r._rowKey, {
        name: 'Missing Last Name',
      });
    } else if (!fn) {
      bump(r._rowKey, {
        name: 'Extra space in name',
      });
    }
  }

  return { issues };
}

function issuesHasErrors(issues) {
  return Object.keys(issues).some((k) => {
    const i = issues[k];
    return Boolean(i?.row || i?.first_name || i?.last_name || i?.name);
  });
}

/** Same vertical slot as text inputs so edit vs view-only / deactivated rows line up */
const FIELD_ROW_MIN = 'min-h-[2.125rem]';

const PERM_SELECT_CLASS =
  `w-full ${FIELD_ROW_MIN} rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500`;

const INPUT_CLASS =
  `w-full ${FIELD_ROW_MIN} rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500`;

/** Read-only mirror of input chrome (padding, border, radius, min height) */
const READONLY_FIELD_CLASS = `${FIELD_ROW_MIN} flex w-full min-w-0 items-center rounded-md border border-gray-300 bg-gray-50 px-2 py-1 text-sm text-gray-900`;

/** Same horizontal + vertical padding on th and td so headers line up with values */
const TABLE_PAD = 'px-3 py-2 text-left align-middle border-b border-gray-200';
const TH_CELL = `${TABLE_PAD} text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-50`;
const TD_CELL = `${TABLE_PAD} text-sm text-gray-900`;

export default function UsersTable({
  organizationId,
  organization,
  profile,
  sessionUserEmail,
  canEdit,
}) {
  const [rows, setRows] = useState([]);
  const [lastSavedRows, setLastSavedRows] = useState([]);
  const [baselineById, setBaselineById] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [tableNotice, setTableNotice] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [csvInput, setCsvInput] = useState('');
  const [csvImportError, setCsvImportError] = useState('');
  const [csvDefaults, setCsvDefaults] = useState({
    user_management_permission: NEW_ROW_DEFAULTS.user_management_permission,
    product_permission: NEW_ROW_DEFAULTS.product_permission,
    integration_permission: NEW_ROW_DEFAULTS.integration_permission,
    analytics_permission: NEW_ROW_DEFAULTS.analytics_permission,
  });

  const associatedDomains = organization?.associated_domains ?? EMPTY_DOMAINS;
  const hasAnalytics =
    organization?.permissions?.analytics_user_management != null
      ? Boolean(organization.permissions.analytics_user_management)
      : organization?.permissions?.analytics != null;
  const isPrimaryOrg = organizationId === profile?.primary_organization_id;
  const showAnalytics = hasAnalytics && isPrimaryOrg;
  const analyticsEditLimit =
    isPrimaryOrg && typeof organization?.analytics_edit_user_limit === 'number'
      ? organization.analytics_edit_user_limit
      : null;
  const analyticsReadOnlyEnabled = isPrimaryOrg && Boolean(organization?.analytics_read_only_enabled);
  const canEditAnalytics = canEdit && isPrimaryOrg;

  const loadProfiles = useCallback(async (forceFresh = false) => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const existing = listUsersInFlightByOrg.get(organizationId);
      const requestPromise = !forceFresh && existing ? existing : listOrganizationProfilesV1(organizationId);
      listUsersInFlightByOrg.set(organizationId, requestPromise);
      const { data, error } = await requestPromise;
      if (listUsersInFlightByOrg.get(organizationId) === requestPromise) {
        listUsersInFlightByOrg.delete(organizationId);
      }
      if (error) throw error;
      const list = Array.isArray(data) ? data : [];
      const mapped = list.map(mapProfileToRow);
      setRows(mapped);
      setLastSavedRows(mapped.map((r) => JSON.parse(JSON.stringify({ ...r, _rowError: null, _fieldErrors: null }))));
      setTableNotice(null);
      const baseline = {};
      mapped.forEach((r) => {
        if (r.id) baseline[r.id] = rowBaseline(r);
      });
      setBaselineById(baseline);
    } catch (e) {
      console.error(e);
      setTableNotice({ tone: 'error', text: 'Could not load organization users.' });
      setRows([]);
      setLastSavedRows([]);
      setBaselineById({});
      if (listUsersInFlightByOrg.get(organizationId) === requestPromise) {
        listUsersInFlightByOrg.delete(organizationId);
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const normalizedSelfEmail = useMemo(() => normalizeEmail(sessionUserEmail), [sessionUserEmail]);

  const isCurrentUserRow = useCallback(
    (row) => {
      if (row.id && profile?.id && row.id === profile.id) return true;
      const em = normalizeEmail(row.email);
      return Boolean(em && normalizedSelfEmail && em === normalizedSelfEmail);
    },
    [profile?.id, normalizedSelfEmail]
  );

  const analyticsEditCount = useMemo(() => countAnalyticsEditSeats(rows), [rows]);
  const baselineAnalyticsEditCount = useMemo(() => countAnalyticsEditSeats(lastSavedRows), [lastSavedRows]);
  const blocksOnAnalyticsLimit = useMemo(() => {
    if (analyticsEditLimit == null || analyticsEditLimit < 0) return false;
    if (analyticsEditCount <= analyticsEditLimit) return false;
    // If org is already at/over limit from saved state, allow edits that do not increase seat usage.
    return analyticsEditCount > baselineAnalyticsEditCount;
  }, [analyticsEditLimit, analyticsEditCount, baselineAnalyticsEditCount]);

  const updateRow = (rowKey, patch) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._rowKey !== rowKey) return r;
        const nextFe = r._fieldErrors ? { ...r._fieldErrors } : {};
        if ('first_name' in patch || 'last_name' in patch) {
          delete nextFe.first_name;
          delete nextFe.last_name;
          delete nextFe.name;
        }
        const fieldErrors = Object.keys(nextFe).length ? nextFe : null;
        return {
          ...r,
          ...patch,
          _rowError: null,
          _fieldErrors: fieldErrors,
        };
      })
    );
  };

  const handleAddRow = () => {
    if (!associatedDomains.length) return;
    setRows((prev) => [...prev, newDraftRow(`new-${Date.now()}`)]);
  };

  const handleRemoveDraft = (rowKey) => {
    setRows((prev) => prev.filter((r) => !(r._rowKey === rowKey && r._isNew)));
  };

  const handleImportCsvUsers = () => {
    if (!canAddRows) return;
    const { rows: parsed, error } = parseCsvUsers(csvInput);
    if (error) {
      setCsvImportError(error);
      return;
    }
    setCsvImportError('');

    const existingEmails = new Set(rows.map((r) => normalizeEmail(r.email)).filter(Boolean));
    let key = Date.now();
    const toAdd = [];
    let ignored = 0;
    for (const item of parsed) {
      if (existingEmails.has(item.email)) {
        ignored += 1;
        continue;
      }
      existingEmails.add(item.email);
      const dr = newDraftRow(`new-${key++}`);
      const { first_name, last_name } = splitFullName(item.name);
      dr.email = item.email;
      dr.first_name = first_name;
      dr.last_name = last_name;
      dr.user_management_permission = csvDefaults.user_management_permission;
      dr.product_permission = csvDefaults.product_permission;
      dr.integration_permission = csvDefaults.integration_permission;
      dr.analytics_permission =
        showAnalytics && canEditAnalytics ? csvDefaults.analytics_permission : NEW_ROW_DEFAULTS.analytics_permission;
      toAdd.push(dr);
    }

    if (!toAdd.length) {
      setCsvImportError('All pasted emails already exist in the table.');
      return;
    }

    setRows((prev) => [...prev, ...toAdd]);
    setCsvInput('');
    setCsvImportError('');
    setBulkModalOpen(false);
  };

  const handleRevert = useCallback(() => {
    setRows((prev) => {
      const drafts = prev.filter((r) => r._isNew);
      const restored = lastSavedRows.map((r) => ({
        ...r,
        _rowError: null,
        _fieldErrors: null,
      }));
      return [...restored, ...drafts];
    });
  }, [lastSavedRows]);

  /** Edits to server-backed rows only (draft rows are left unchanged by revert) */
  const hasRevertibleEdits = useMemo(() => {
    const byId = new Map(lastSavedRows.map((r) => [r.id, r]));
    for (const r of rows) {
      if (r._isNew) continue;
      const s = byId.get(r.id);
      if (!s) return true;
      if (JSON.stringify(rowBaseline(r)) !== JSON.stringify(rowBaseline(s))) return true;
      if (r.email !== s.email) return true;
      if (r.requires_password_set !== s.requires_password_set) return true;
      if (r.last_sign_in_at !== s.last_sign_in_at) return true;
    }
    return false;
  }, [rows, lastSavedRows]);

  const rowAnalysis = useMemo(() => analyzeRows(rows, associatedDomains), [rows, associatedDomains]);

  const hasValidationErrors = issuesHasErrors(rowAnalysis.issues);

  const hasSaveableWork = useMemo(() => {
    if (rows.some(newDraftIsTouched)) return true;
    return rows.some((r) => {
      if (r._isNew) return false;
      const b = baselineById[r.id];
      if (!b) return false;
      return JSON.stringify(rowBaseline(r)) !== JSON.stringify(b);
    });
  }, [rows, baselineById]);

  const canSave =
    hasSaveableWork && !hasValidationErrors && !blocksOnAnalyticsLimit;
  const saveDisabledMessage =
    saving
      ? ''
      : blocksOnAnalyticsLimit
          ? `Analytics edit user limit reached (${analyticsEditCount}/${analyticsEditLimit}).`
          : hasValidationErrors
            ? 'Fix validation errors before saving.'
            : '';

  const handleEmailPaste = (e, rowKey, row) => {
    if (!row._isNew || !associatedDomains.length) return;
    const text = e.clipboardData?.getData('text') ?? '';
    const emails = parseEmailList(text);
    if (emails.length <= 1) return;
    e.preventDefault();
    const [first, ...rest] = emails;
    setRows((prev) => {
      const next = prev.map((r) =>
        r._rowKey === rowKey ? { ...r, email: normalizeEmail(first), _rowError: null } : r
      );
      const existing = new Set(next.map((r) => normalizeEmail(r.email)).filter(Boolean));
      let key = Date.now();
      const toAdd = [];
      for (const em of rest) {
        const n = normalizeEmail(em);
        if (!n || existing.has(n)) continue;
        existing.add(n);
        const dr = newDraftRow(`new-${key++}`);
        dr.email = n;
        toAdd.push(dr);
      }
      return [...next, ...toAdd];
    });
  };

  const validateRowsForSave = () => {
    const { issues } = analyzeRows(rows, associatedDomains);
    if (blocksOnAnalyticsLimit) {
      setTableNotice({
        tone: 'error',
        text: `This organization allows at most ${analyticsEditLimit} user(s) with analytics edit access (including pending invitations).`,
      });
      return null;
    }
    return issues;
  };

  const handleSave = async () => {
    if (!canEdit || !organizationId) return;
    if (!canSave) return;

    const validationIssues = validateRowsForSave();
    if (validationIssues === null) return;

    const hasValErr = Object.keys(validationIssues).some((k) => {
      const i = validationIssues[k];
      return Boolean(i?.row || i?.first_name || i?.last_name || i?.name);
    });
    if (hasValErr) {
      setRows((prev) =>
        prev.map((x) => {
          const i = validationIssues[x._rowKey];
          if (!i) return { ...x, _rowError: null, _fieldErrors: null };
          const fe = {};
          if (i.first_name) fe.first_name = i.first_name;
          if (i.last_name) fe.last_name = i.last_name;
          if (i.name) fe.name = i.name;
          return {
            ...x,
            _rowError: i.row ?? null,
            _fieldErrors: Object.keys(fe).length ? fe : null,
          };
        })
      );
      setTableNotice({ tone: 'error', text: 'Fix the highlighted fields before saving.' });
      return;
    }

    const invitees = [];

    for (const r of rows) {
      const em = normalizeEmail(r.email);

      if (r._isNew) {
        if (!em) continue;
        invitees.push({
          email: em,
          first_name: r.first_name.trim(),
          last_name: r.last_name.trim(),
          is_active: r.is_active,
          user_management_permission: r.user_management_permission === '' ? null : r.user_management_permission,
          product_permission: r.product_permission === '' ? null : r.product_permission,
          integration_permission: r.integration_permission === '' ? null : r.integration_permission,
          analytics_permission: r.analytics_permission === '' ? null : r.analytics_permission,
        });
        continue;
      }

      const base = baselineById[r.id];
      if (!base) continue;
      const current = rowBaseline(r);
      const changed =
        current.first_name !== base.first_name ||
        current.last_name !== base.last_name ||
        current.is_active !== base.is_active ||
        current.user_management_permission !== base.user_management_permission ||
        current.product_permission !== base.product_permission ||
        current.integration_permission !== base.integration_permission ||
        current.analytics_permission !== base.analytics_permission;

      if (changed) {
        const patch = buildInviteeForUpdate(em, base, r);
        if (Object.keys(patch).length > 1) {
          invitees.push(patch);
        }
      }
    }

    if (invitees.length === 0) {
      setTableNotice({ tone: 'info', text: 'Nothing to save.' });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await bulkUpsertUsersV1(organizationId, invitees);
      if (error) throw error;
      const results = Array.isArray(data) ? data : [];

      const byEmail = new Map(results.map((x) => [normalizeEmail(x.email), x]));
      const ok = results.filter((x) => x.success).length;
      const fail = results.filter((x) => !x.success).length;

      setRows((prev) =>
        prev.map((r) => {
          const em = normalizeEmail(r.email);
          const res = byEmail.get(em);
          if (!res) return { ...r, _rowError: null, _fieldErrors: null };
          if (res.success) {
            return { ...r, _rowError: null, _fieldErrors: null };
          }
          let msg = res.error || 'Update failed';
          if (res.error_code === '22023') msg = res.error || 'Invalid email';
          if (res.error_code === '23505') msg = 'Email conflict — please retry.';
          if (res.error_code === '23514' && String(res.error || '').includes('analytics edit user limit')) {
            msg = res.error || 'Analytics edit user limit reached.';
          }
          if (res.error_code === '42501') msg = res.error || 'Permission denied.';
          return { ...r, _rowError: msg, _fieldErrors: null };
        })
      );

      if (ok > 0 && fail === 0) setTableNotice({ tone: 'success', text: `${ok} user row(s) saved.` });
      if (ok > 0 && fail > 0) setTableNotice({ tone: 'error', text: `${ok} saved, ${fail} failed. See rows.` });
      if (ok === 0 && fail > 0) setTableNotice({ tone: 'error', text: `${fail} row(s) failed. See rows.` });

      await loadProfiles(true);
    } catch (e) {
      console.error(e);
      setTableNotice({ tone: 'error', text: e.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  /** Existing users (filtered), then new draft rows (search only), always at bottom */
  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchesSearch = (r) => {
      if (!q) return true;
      const fullName = fullNameFromParts(r.first_name, r.last_name).toLowerCase();
      return (
        normalizeEmail(r.email).includes(q) ||
        (r.first_name || '').toLowerCase().includes(q) ||
        (r.last_name || '').toLowerCase().includes(q) ||
        fullName.includes(q)
      );
    };
    const matchesStatus = (r) => {
      if (r._isNew) return true;
      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return r.is_active === true;
      if (statusFilter === 'never_signed_in') return Boolean(r.is_active && !r.last_sign_in_at);
      if (statusFilter === 'deactivated') return r.is_active === false;
      if (statusFilter === 'analytics_editors') return r.analytics_permission === 'edit';
      if (statusFilter === 'user_admins') return r.user_management_permission === 'edit';
      if (statusFilter === 'product_admins') return r.product_permission === 'edit';
      if (statusFilter === 'integration_admins') return r.integration_permission === 'edit';
      return true;
    };
    const existing = rows.filter((r) => !r._isNew && matchesStatus(r) && matchesSearch(r));
    const drafts = rows.filter((r) => r._isNew && matchesSearch(r));
    return [...existing, ...drafts];
  }, [rows, search, statusFilter]);

  const permissionLabel = (v) => {
    if (!v) return '—';
    return v === 'view' ? 'View' : 'Edit';
  };

  const canAddRows = canEdit && associatedDomains.length > 0;

  const permissionSelect = (row, field) => {
    const self = isCurrentUserRow(row);
    const disabledUserMgmt = field === 'user_management_permission' && self;
    const deactivated = !row._isNew && profileStatus(row) === 'deactivated';
    let disabled = !canEdit || deactivated;
    if (field === 'analytics_permission' && !canEditAnalytics) disabled = true;
    if (field === 'user_management_permission') disabled = disabled || disabledUserMgmt;

    if (!canEdit) {
      return <div className={READONLY_FIELD_CLASS}>{permissionLabel(row[field])}</div>;
    }

    return (
      <select
        className={PERM_SELECT_CLASS}
        autoComplete="off"
        value={row[field]}
        disabled={disabled}
        onChange={(e) => {
          updateRow(row._rowKey, { [field]: e.target.value });
        }}
      >
        <option value="">None</option>
        {field === 'analytics_permission' ? (
          <>
            {(analyticsReadOnlyEnabled || row.analytics_permission === 'view') && (
              <option value="view" disabled={!analyticsReadOnlyEnabled}>
                View
              </option>
            )}
            <option value="edit">Edit</option>
          </>
        ) : (
          <>
            <option value="view">View</option>
            <option value="edit">Edit</option>
          </>
        )}
      </select>
    );
  };

  if (loading) {
    return <p className="text-sm text-gray-600 py-8">Loading users…</p>;
  }

  const emptyColSpan = 5 + (showAnalytics ? 1 : 0);

  const renderActiveCell = (row) => {
    if (row._isNew && canEdit) {
      return (
        <div className={`flex ${FIELD_ROW_MIN} w-full items-center justify-center`}>
          <button
            type="button"
            onClick={() => handleRemoveDraft(row._rowKey)}
            title="Remove draft row"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-primaryBrandColor"
            aria-label="Remove draft row"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      );
    }

    const isSelf = isCurrentUserRow(row);
    const disableDeactivateSelf = canEdit && isSelf;
    const selfTitle = disableDeactivateSelf ? 'You cannot deactivate your own account.' : 'Account active';
    return (
      <div className={`flex ${FIELD_ROW_MIN} w-full items-center justify-center`}>
        {!canEdit ? (
          <span className="text-sm font-medium text-gray-800">{row.is_active !== false ? 'Yes' : 'No'}</span>
        ) : (
          <input
            type="checkbox"
            autoComplete="off"
            checked={row.is_active !== false}
            disabled={disableDeactivateSelf}
            onChange={(e) => updateRow(row._rowKey, { is_active: e.target.checked })}
            title={selfTitle}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-primaryBrandColor disabled:cursor-not-allowed disabled:opacity-60"
          />
        )}
      </div>
    );
  };

  return (
    <div className="mt-6">
      <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <div className="mb-5 space-y-2">
        <p className="text-sm text-gray-800">
          <span className="text-gray-600">Allowed email domains:</span>{' '}
          <span className="font-bold text-gray-900">
            {associatedDomains.length > 0 ? associatedDomains.join(', ') : 'None configured'}
          </span>
        </p>
        {associatedDomains.length === 0 && (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            This organization has no associated domains configured. You cannot add users until at least one domain
            is set up for this organization.
          </p>
        )}
      </div>

      {analyticsEditLimit != null && (
        <p className="mt-3 text-xs text-gray-600">
          Used analytics edit seats:{' '}
          <span className="font-medium">
            {analyticsEditCount} / {analyticsEditLimit === -1 ? 'Unlimited' : analyticsEditLimit}
          </span>
        </p>
      )}
      {tableNotice && (
        <div
          className={`mt-3 rounded-md border px-3 py-2 text-sm ${
            tableNotice.tone === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : tableNotice.tone === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-blue-200 bg-blue-50 text-blue-800'
          }`}
        >
          {tableNotice.text}
        </div>
      )}

      <div className="mt-2 flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-end gap-3">
          <label className="flex flex-col text-sm text-gray-700">
            <span className="mb-1 font-medium">Search</span>
            <input
              type="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or email"
              className="h-9 rounded-md border border-gray-300 px-3 text-sm shadow-sm w-56"
            />
          </label>
          <div
            className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5"
            role="group"
            aria-label="Filter users by access state"
          >
            {[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'never_signed_in', label: 'Never signed in' },
              { value: 'deactivated', label: 'Deactivated' },
              ...(showAnalytics ? [{ value: 'analytics_editors', label: 'Analytics Editors' }] : []),
              { value: 'user_admins', label: 'User Admins' },
              { value: 'product_admins', label: 'Product Admins' },
              { value: 'integration_admins', label: 'Integration Admins' },
            ].map((opt) => {
              const on = statusFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                    on
                      ? 'border-primaryBrandColor bg-secondaryBrandColor text-gray-900'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        {canEdit && (
          <div className="flex shrink-0 flex-nowrap items-start gap-2 min-h-[2.5rem]">
            <div className="relative flex flex-col items-stretch gap-1">
              <button
                type="button"
                onClick={handleRevert}
                disabled={!hasRevertibleEdits}
                title={hasRevertibleEdits ? 'Discard unsaved changes to saved users (keeps new rows)' : undefined}
                className={`${secondaryButtonClasses} ${
                  hasRevertibleEdits
                    ? ''
                    : 'invisible pointer-events-none'
                }`}
              >
                <ArrowUturnLeftIcon className="h-4 w-4 inline -mt-0.5 mr-1" aria-hidden="true" />
                Revert
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !canSave}
                title={
                  !hasSaveableWork
                    ? 'Add or edit a user first'
                    : hasValidationErrors || blocksOnAnalyticsLimit
                      ? 'Fix validation errors before saving'
                      : undefined
                }
                className={`${primaryButtonClasses} min-w-[8.5rem] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-secondaryBrandColor disabled:hover:text-primaryBrandColor`}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {saveDisabledMessage ? (
                <p className="pointer-events-none absolute left-0 top-full z-20 mt-1 max-w-[16rem] rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] leading-snug text-amber-800 shadow-sm">
                  {saveDisabledMessage}
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className="mt-2 flow-root">
        <div className="-mx-4 overflow-x-auto [scrollbar-gutter:stable] sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 pb-8 align-middle sm:px-6 lg:px-8 min-h-[min(70vh,28rem)]">
            <table
              className={`w-full table-fixed border-collapse ${
                showAnalytics ? 'min-w-[72rem]' : 'min-w-[63rem]'
              }`}
            >
              <colgroup>
                <col className={showAnalytics ? 'w-[28%]' : 'w-[32%]'} />
                {showAnalytics && <col className="w-[13%]" />}
                <col className={showAnalytics ? 'w-[14.5%]' : 'w-[17%]'} />
                <col className={showAnalytics ? 'w-[14.5%]' : 'w-[17%]'} />
                <col className={showAnalytics ? 'w-[14.5%]' : 'w-[17%]'} />
                <col className="w-[2.75rem]" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className={`${TH_CELL} pl-4 sm:pl-3`}>
                    User
                  </th>
                  {showAnalytics && <th scope="col" className={TH_CELL}>Analytics</th>}
                  <th scope="col" className={TH_CELL}>
                    Integrations
                  </th>
                  <th scope="col" className={TH_CELL}>
                    Products
                  </th>
                  <th scope="col" className={TH_CELL}>
                    Users
                  </th>
                  <th scope="col" className={`${TH_CELL} text-center`}>
                    Active
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {displayRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={emptyColSpan}
                      className={`${TD_CELL} py-16 text-center text-sm text-gray-500 align-middle min-h-[16rem]`}
                    >
                      No users match the current filters.
                    </td>
                  </tr>
                )}
                {displayRows.map((row, idx) => {
                  const st = row._isNew ? 'new' : profileStatus(row);
                  const rowClass = row._rowError ? 'bg-red-50' : idx % 2 === 1 ? 'bg-gray-50/80' : 'bg-white';
                  const fe = row._fieldErrors || {};
                  const liveIssue = rowAnalysis.issues[row._rowKey];
                  const liveNameErr = liveIssue?.name;
                  const liveRowErr = liveIssue?.row;
                  const nameErr = liveNameErr || fe.name;
                  const emailErrBorder = Boolean(liveRowErr || row._rowError);

                  const chEmail = isFieldChanged(row, 'email', baselineById);
                  const chUser = chEmail || nameFieldsChanged(row, baselineById);
                  const chActive = isFieldChanged(row, 'is_active', baselineById);
                  const chUm = isFieldChanged(row, 'user_management_permission', baselineById);
                  const chProd = isFieldChanged(row, 'product_permission', baselineById);
                  const chInt = isFieldChanged(row, 'integration_permission', baselineById);
                  const chAn = isFieldChanged(row, 'analytics_permission', baselineById);

                  const lastSigned = formatLastSignIn(row.last_sign_in_at);

                  /** Fills flex gap so zebra / highlight row bg does not show as a dark strip between white inputs */
                  const userStackBg = row._rowError
                    ? 'bg-red-50'
                    : chUser
                      ? 'bg-amber-50'
                      : idx % 2 === 1
                        ? 'bg-gray-50/80'
                        : 'bg-white';

                  return (
                    <tr key={row._rowKey} className={rowClass}>
                      <td
                        className={`${TD_CELL} pl-4 sm:pl-3 ${
                          row._rowError ? '' : chUser ? CELL_CHANGED : ''
                        }`}
                      >
                        <div className={`flex min-w-0 flex-col gap-1 ${userStackBg}`}>
                          {canEdit && st !== 'deactivated' ? (
                            <div className="relative">
                              <input
                                type="text"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                placeholder="First Last (space between)"
                                className={`${INPUT_CLASS} ${nameErr ? 'border-red-500 ring-1 ring-red-200' : ''}`}
                                value={editableNameValue(row)}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const { first_name, last_name } = splitFullName(raw);
                                  updateRow(row._rowKey, { first_name, last_name, _nameInput: raw });
                                }}
                                onBlur={(e) => {
                                  const { first_name, last_name } = splitFullName(e.target.value);
                                  updateRow(row._rowKey, { first_name, last_name, _nameInput: null });
                                }}
                              />
                              <ValidationPopunder message={nameErr} />
                            </div>
                          ) : (
                            <div className={READONLY_FIELD_CLASS}>
                              <span className="min-w-0 truncate font-medium leading-snug">
                                {fullNameFromParts(row.first_name, row.last_name) || '—'}
                              </span>
                            </div>
                          )}
                          {row._isNew ? (
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="email"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                className={`${INPUT_CLASS} ${emailErrBorder ? 'border-red-500 ring-1 ring-red-200' : ''}`}
                                value={row.email}
                                placeholder="Email address"
                                onChange={(e) => updateRow(row._rowKey, { email: e.target.value })}
                                onBlur={(e) => updateRow(row._rowKey, { email: normalizeEmail(e.target.value) })}
                                onPaste={(e) => handleEmailPaste(e, row._rowKey, row)}
                              />
                              <ValidationPopunder message={row._rowError ?? liveRowErr} />
                            </div>
                          ) : (
                            <div className={`relative ${READONLY_FIELD_CLASS}`}>
                              <span
                                className="min-w-0 truncate font-medium leading-snug"
                                title={row.email || undefined}
                              >
                                {row.email}
                              </span>
                              <ValidationPopunder message={row._rowError ?? liveRowErr} />
                            </div>
                          )}
                          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs text-gray-500">
                            <span className="min-w-0">
                              Last signed in:{' '}
                              <span className="font-medium text-gray-700">
                                {row._isNew ? '—' : lastSigned ?? 'Never'}
                              </span>
                            </span>
                          </div>
                        </div>
                      </td>
                      {showAnalytics && (
                        <td className={`${TD_CELL} ${chAn ? CELL_CHANGED : ''}`}>
                          {permissionSelect(row, 'analytics_permission')}
                        </td>
                      )}
                      <td className={`${TD_CELL} ${chInt ? CELL_CHANGED : ''}`}>
                        {permissionSelect(row, 'integration_permission')}
                      </td>
                      <td className={`${TD_CELL} ${chProd ? CELL_CHANGED : ''}`}>
                        {permissionSelect(row, 'product_permission')}
                      </td>
                      <td className={`${TD_CELL} ${chUm ? CELL_CHANGED : ''}`}>
                        {permissionSelect(row, 'user_management_permission')}
                      </td>
                      <td
                        className={`${TD_CELL} w-[2.75rem] max-w-[2.75rem] px-1.5 align-middle ${
                          !row._rowError && chActive ? CELL_CHANGED : ''
                        }`}
                      >
                        {renderActiveCell(row)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="mt-3 grid gap-2">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setCsvImportError('');
                setBulkModalOpen(true);
              }}
              disabled={!canAddRows}
              title={!canAddRows ? 'Configure associated domains before adding users.' : undefined}
              className={secondaryButtonClasses}
            >
              Bulk Add Users
            </button>
            <button
              type="button"
              onClick={handleAddRow}
              disabled={!canAddRows}
              title={!canAddRows ? 'Configure associated domains before adding users.' : undefined}
              className={secondaryButtonClasses}
            >
              + Add User
            </button>
          </div>
        </div>
      )}

      {canEdit && bulkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-lg bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Bulk Add Users</h3>
                <p className="mt-0.5 text-xs text-gray-600">Paste one user per line: email,name</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCsvImportError('');
                  setBulkModalOpen(false);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                aria-label="Close bulk add users modal"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { key: 'user_management_permission', label: 'Users' },
                { key: 'product_permission', label: 'Products' },
                { key: 'integration_permission', label: 'Integrations' },
                ...(showAnalytics ? [{ key: 'analytics_permission', label: 'Analytics' }] : []),
              ].map((opt) => (
                <label key={opt.key} className="flex flex-col gap-1 text-xs text-gray-700">
                  <span className="font-medium">{opt.label}</span>
                  <select
                    className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    value={csvDefaults[opt.key]}
                    disabled={opt.key === 'analytics_permission' && !canEditAnalytics}
                    onChange={(e) => setCsvDefaults((prev) => ({ ...prev, [opt.key]: e.target.value }))}
                  >
                    <option value="">None</option>
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                  </select>
                </label>
              ))}
            </div>
            <textarea
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
              placeholder={'email,name\njane@example.com,Jane Doe'}
              className="mt-3 w-full rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              rows={8}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            {csvImportError ? (
              <p className="mt-2 text-xs text-red-700">{csvImportError}</p>
            ) : null}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCsvImportError('');
                  setBulkModalOpen(false);
                }}
                className={secondaryButtonClasses}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImportCsvUsers}
                disabled={!canAddRows}
                title={!canAddRows ? 'Configure associated domains before adding users.' : undefined}
                className={primaryButtonClasses}
              >
                Import Users
              </button>
            </div>
          </div>
        </div>
      )}

      {!canEdit && (
        <p className="mt-4 text-sm text-gray-600">You have view-only access. Contact an admin to change users.</p>
      )}
      </form>
    </div>
  );
}
